// BeaconUpgrade.jsx  →  src/components/Display/MagicBand/BeaconUpgrade.jsx
// MagicBeacon package activation (display + TV). Opened from Settings via
// screen_view "BAND__activate". Same server flow as before:
//   POST /integrations/beacon-upgrade   { device_id, phone_last_4_digits }
//   GET  /integrations/beacon-upgrade?device_id=   (status polling)
// Once the add-on syncs into Mongo (hasBeaconAddOn flips), we jump to BAND,
// which shows the pairing screen.
//
// Two short steps (display + TV): 1) the "I understand" box only; tapping it
// (OK on TV) checks it and slides to 2) last-4 digits on an on-screen keypad.
// TV: lime focus ring, arrows move, OK selects, Back goes back, remote number
// keys type digits.

import { useEffect, useState, useRef } from "react";
import { IoCloseSharp } from "react-icons/io5";
import useGlobalStore from "../../../store/global.store";
import axios from "../../../config/axios";
import { useRemote } from "../../../hooks/useRemote";
import { useTV } from "../../../hooks/useTV";

const LIME = "#3ADF3C";
const BLUE = "#007bff";
const NAVY = "#1B2550";

const BeaconUpgrade = ({ device }) => {
  const { isTv } = useTV();
  const { updateView } = useRemote();
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const hasBeaconAddOn = useGlobalStore((state) => state.hasBeaconAddOn);
  const hasVipAddOn = useGlobalStore((state) => state.hasVipAddOn);

  const [loading, setLoading] = useState(false);
  const [upgradeRequest, setUpgradeRequest] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [agreementError, setAgreementError] = useState("");
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);
  const [phoneLastDigits, setPhoneLastDigits] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [formStep, setFormStep] = useState("agree"); // vip | agree | digits
  const [addVip, setAddVip] = useState(false);
  const [vipSent, setVipSent] = useState(null); // null | "sent" | error message
  const advanceTimer = useRef(null);
  const statusCheckerInterval = useRef(null);

  // Billing period from the plan price ID (same rule as the server).
  const subName = String(device?.subscription?.subscription_name || "").toLowerCase();
  const isSixMonth = subName.endsWith("-6m") || subName.includes("every-6-months");
  const isMonthly = !isSixMonth && subName.includes("month");
  const addonPrice = isSixMonth ? "+$30 every 6 months" : isMonthly ? "+$4.99/month" : "+$55/year";
  const addonEach = isSixMonth ? "+$30 each every 6 months" : isMonthly ? "+$4.99/month each" : "+$55/year each";
  const needsVip = !hasVipAddOn;

  // ===== Server status (unchanged behavior) =====
  const checkUpgradeStatus = () => {
    if (!device._id) return;
    setCheckingStatus(true);
    axios
      .get("/integrations/beacon-upgrade", { params: { device_id: device._id } })
      .then((res) => {
        setUpgradeRequest(res.data);
        if (
          res.data &&
          (res.data.status === "error" ||
            res.data.status === "failed" ||
            res.data.status === "success")
        ) {
          clearInterval(statusCheckerInterval.current);
          statusCheckerInterval.current = null;
        }
      })
      .catch((err) => {
        console.log(err);
        setPhoneError(err.response?.data?.message || "Failed to check status");
      })
      .finally(() => setCheckingStatus(false));
  };

  const startStatusChecker = () => {
    if (statusCheckerInterval.current) clearInterval(statusCheckerInterval.current);
    statusCheckerInterval.current = setInterval(checkUpgradeStatus, 30000);
  };

  useEffect(() => {
    setShowBottomBar(false);
    setLoading(true);
    axios
      .get("/integrations/beacon-upgrade", { params: { device_id: device._id } })
      .then((res) => {
        setUpgradeRequest(res.data);
        if (res.data && (res.data.status === "pending" || res.data.status === "preparing")) {
          startStatusChecker();
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => {
      if (statusCheckerInterval.current) clearInterval(statusCheckerInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jump to BAND (pairing screen) as soon as the add-on lands in Mongo.
  useEffect(() => {
    if (!hasBeaconAddOn) return;
    if (showUpgradeForm || submitting) return;
    updateView("BAND", () => setShowBottomBar(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeaconAddOn, showUpgradeForm, submitting]);

  const handleSubmit = () => {
    let hasError = false;
    if (phoneLastDigits.length !== 4) {
      setPhoneError("Please enter the last 4 digits of your phone number");
      hasError = true;
    } else setPhoneError("");
    if (!priceConfirmed) {
      setAgreementError("Please confirm the terms to activate the MagicBeacon package");
      hasError = true;
    } else setAgreementError("");
    if (hasError) return;

    setSubmitting(true);
    setCheckingStatus(true);
    axios
      .post("/integrations/beacon-upgrade", {
        device_id: device._id,
        phone_last_4_digits: phoneLastDigits,
      })
      .then(() => {
        if (addVip) {
          axios
            .post("/integrations/vip-upgrade", {
              device_id: device._id,
              phone_last_4_digits: phoneLastDigits,
            })
            .then(() => setVipSent("sent"))
            .catch((err) => setVipSent(err.response?.data?.message || "Something went wrong"));
        }
        setShowUpgradeForm(false);
        setSubmitting(false);
        checkUpgradeStatus();
        startStatusChecker();
      })
      .catch((err) => {
        setPhoneError(err.response?.data?.message || "Invalid phone number or something went wrong");
        setSubmitting(false);
        setCheckingStatus(false);
      });
  };

  const handleClose = () => updateView("Home", () => setShowBottomBar(true));
  const handleOrder = () => updateView("BAND__upgrade", () => setShowBottomBar(true));
  const openForm = () => {
    setShowUpgradeForm(true);
    setFormStep(needsVip ? "vip" : "agree");
    setAddVip(false);
    setVipSent(null);
    setPriceConfirmed(false);
    setPhoneLastDigits("");
    setPhoneError("");
    setAgreementError("");
  };
  // Checking the box shows the ✓ for a moment, then slides to the digits.
    const agree = () => {
    setAgreementError("");
    if (!isTv) {
      // Display: tap toggles the box; they press NEXT to continue.
      setPriceConfirmed((v) => !v);
      return;
    }
    setPriceConfirmed(true);
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => setFormStep("digits"), 450);
  };
  const goNext = () => {
    if (!priceConfirmed) {
      setAgreementError("Please confirm the terms to activate the MagicBeacon package");
      return;
    }
    setFormStep("digits");
  };
  const formBack = () => {
    clearTimeout(advanceTimer.current);
    if (formStep === "digits") {
      setFormStep("agree");
      setPhoneLastDigits("");
      setPhoneError("");
        } else if (formStep === "agree" && needsVip) {
      setFormStep("vip");
      setPriceConfirmed(false);
      setAgreementError("");
    } else {
      setShowUpgradeForm(false);
    }
  };
  useEffect(() => () => clearTimeout(advanceTimer.current), []);
  const retry = () => {
    setUpgradeRequest(null);
    setCheckingStatus(false);
    setSubmitting(false);
    setPhoneError("");
    setAgreementError("");
    setPhoneLastDigits("");
    setPriceConfirmed(false);
    setFormStep(needsVip ? "vip" : "agree");
    setAddVip(false);
    setVipSent(null);
    setShowUpgradeForm(true);
  };
  const pressKey = (k) => {
    setPhoneError("");
    if (k === "del") setPhoneLastDigits((d) => d.slice(0, -1));
    else if (k === "clear") setPhoneLastDigits("");
    else setPhoneLastDigits((d) => (d.length < 4 ? d + k : d));
  };

  // ===== Which screen are we on? =====
  const screen = loading
    ? "loading"
    : upgradeRequest
    ? "status"
    : showUpgradeForm && !submitting
    ? "form"
    : checkingStatus || submitting
    ? "processing"
    : "intro";
  const status = upgradeRequest?.status;

  // Focus grid per screen (TV). Rows are navigated with Up/Down, items with
  // Left/Right. The intro is a single row, so Up/Down do nothing there.
  const layout =
    screen === "intro"
      ? [["activate", "order", "back"]]
        : screen === "form"
        ? formStep === "vip"
        ? [["vipboth", "viponly"], ["formback"]]
        : formStep === "agree"
        ? [["agree"], ["formback"]]
        : [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"], ["clear", "0", "del"], ["submit", "formback"]]
      : screen === "status"
      ? status === "failed"
        ? [["retry", "close"]]
        : [["close"]]
      : [];

  const actions = {
    activate: openForm,
    order: handleOrder,
    back: handleClose,
    agree,
    submit: handleSubmit,
    formback: formBack,
    vipboth: () => { setAddVip(true); setFormStep("agree"); },
    viponly: () => { setAddVip(false); setFormStep("agree"); },
    retry,
    close: handleClose,
  };
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "clear", "del"].forEach((k) => {
    actions[k] = () => pressKey(k);
  });

  // Default focus when the screen changes.
  useEffect(() => {
    if (!isTv) return;
    if (screen === "intro") setFocusId("activate");
    else if (screen === "form") setFocusId(formStep === "vip" ? "vipboth" : formStep === "agree" ? "agree" : "1");
    else if (screen === "status") setFocusId(status === "failed" ? "retry" : "close");
    else setFocusId(null);
  }, [isTv, screen, status, formStep]);

  // Latest state for the key listener (avoids stale closures).
  const navRef = useRef({});
  navRef.current = { screen, layout, actions, focusId, formStep };

  useEffect(() => {
    if (!isTv) return;
    const onKey = (e) => {
      const { screen: sc, layout: lay, actions: act, focusId: fid, formStep: fs } = navRef.current;
      if (sc === "loading" || sc === "processing") return;
      const k = e.key;
      const isBack =
        k === "Escape" || k === "GoBack" || k === "BrowserBack" || e.keyCode === 461 || e.keyCode === 10009;
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };

      if (k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight") {
        stop();
        let r = lay.findIndex((row) => row.includes(fid));
        if (r < 0) {
          if (lay[0]) setFocusId(lay[0][0]);
          return;
        }
        let c = lay[r].indexOf(fid);
        if (k === "ArrowLeft") c = Math.max(0, c - 1);
        if (k === "ArrowRight") c = Math.min(lay[r].length - 1, c + 1);
        if (k === "ArrowUp" || k === "ArrowDown") {
          const nr = Math.max(0, Math.min(lay.length - 1, r + (k === "ArrowDown" ? 1 : -1)));
          if (nr !== r) {
            const from = lay[r].length - 1 || 1;
            c = Math.round((c / from) * (lay[nr].length - 1));
            r = nr;
          }
        }
        setFocusId(lay[r][c]);
        return;
      }
      if (k === "Enter" || k === " ") {
        stop();
        if (fid && act[fid]) act[fid]();
        return;
      }
      if (sc === "form" && fs === "digits" && /^[0-9]$/.test(k)) {
        stop();
        act[k]();
        return;
      }
      if (isBack) {
        stop();
        if (sc === "form") act.formback();
        else act.close ? act.close() : act.back();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isTv]);

  // ===== Styles =====
  const S = {
    title: isTv ? 52 : 34,
    body: isTv ? 32 : 21,
    small: isTv ? 26 : 17,
    btnFont: isTv ? 32 : 23,
    btnPad: isTv ? "14px 34px" : "12px 26px",
    border: isTv ? 5 : 2,
  };
  const btn = (id, variant = "secondary", extra = {}) => {
    const focused = isTv && focusId === id;
    const look =
      variant === "primary"
        ? { background: BLUE, color: "#fff", borderColor: BLUE }
        : variant === "danger"
        ? { background: "#fff", color: "#C62828", borderColor: "#C62828" }
        : { background: "#fff", color: NAVY, borderColor: NAVY };
    return {
      fontFamily: "Lato, sans-serif",
      fontWeight: 900,
      fontSize: S.btnFont,
      padding: S.btnPad,
      borderRadius: 14,
      borderStyle: "solid",
      borderWidth: S.border,
      cursor: "pointer",
      outline: "none",
      whiteSpace: "nowrap",
      ...look,
      ...(focused ? { borderColor: LIME, transform: "scale(1.05)" } : {}),
      transition: "transform .12s ease",
      ...extra,
    };
  };
  const card = (w) => ({
    backgroundColor: "#fff",
    borderRadius: 24,
    padding: isTv ? "48px 56px" : "30px 36px",
    width: w,
    maxWidth: "94vw",
    boxSizing: "border-box",
    textAlign: "center",
    fontFamily: "Lato, sans-serif",
    color: "#222",
  });

    const statusLabel =
    status === "pending" ? "ACTIVATING…" : status === "preparing" ? "SUCCESS! FINISHING SETUP…" : status === "success" ? "ALL SET!" : status === "failed" ? "SOMETHING WENT WRONG" : "CHECKING…";
  const statusColor =
    status === "failed" ? "#e74c3c" : status === "pending" ? "#f39c12" : "#2ecc71";
  const statusBody =
    status === "pending"
      ? "We're adding the MagicBeacon package to this device. This usually takes a minute or two. Please stay on this screen."
      : status === "success"
      ? "Taking you to your Beacon Pairing Code now…"
      : "Your MagicBeacon package is confirmed! We're now finishing setup on this device, which usually takes 5 to 10 minutes. Please stay on this screen. We'll take you straight to your Beacon Pairing Code (Step 2 in your Beacon Setup Guide) as soon as it's ready.";
  const stayNote =
    "Please stay on this screen. As soon as your activation finishes (usually 5–10 minutes), we'll take you straight to your Beacon Pairing Code (Step 2 in your Beacon Setup Guide). Please wait...";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        top: 0,
        left: 0,
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        width: "100vw",
        height: "100vh",
        zIndex: 50,
        overflow: "hidden",
      }}
    >
      <style>{`@keyframes mbuSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes mbuSlide { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: none; } }
.mbu-slide { animation: mbuSlide .35s cubic-bezier(.2,.7,.2,1) both; }`}</style>

      {!isTv && (screen === "form" || screen === "status") && (
        <button
          onClick={handleClose}
          aria-label="Close"
          style={{ position: "absolute", top: "4%", right: "3%", background: "transparent", border: "none", padding: 5, cursor: "pointer" }}
        >
          <IoCloseSharp size={56} color="white" />
        </button>
      )}

      {screen === "loading" && (
        <span style={{ color: "white", fontFamily: "Lato", fontSize: isTv ? 48 : 36, fontWeight: 700 }}>Loading...</span>
      )}

      {/* ===== Intro ===== */}
      {screen === "intro" && (
        <div style={card(isTv ? 1250 : 940)}>
          <h1 style={{ fontSize: S.title, fontWeight: 900, margin: "0 0 18px" }}>MagicBeacon Activation</h1>
          <p style={{ fontSize: S.body, lineHeight: 1.45, color: "#444", margin: "0 0 14px" }}>
            Adding the MagicBeacon package to this device is the first step of activation.{" "}
            <b>Only continue if you already have your MagicBeacon in hand.</b> Don't have one yet? You can order it today!
          </p>
          <p style={{ fontSize: S.small, lineHeight: 1.4, color: "#666", margin: "0 0 28px" }}>
            Each Wait Time device in your home that will control your MagicBeacon needs its own MagicBeacon package.
          </p>
          <div style={{ display: "flex", gap: isTv ? 28 : 16, justifyContent: "center" }}>
            <button style={btn("activate", "primary")} onClick={actions.activate}>
              ACTIVATE NOW
            </button>
            <button style={btn("order")} onClick={actions.order}>
              ORDER MAGICBEACON
            </button>
            <button style={btn("back", "danger")} onClick={actions.back}>
              GO BACK
            </button>
          </div>
        </div>
      )}

            {/* ===== Form step 0: VIP note (only if this device has no VIP) ===== */}
      {screen === "form" && formStep === "vip" && (
        <div key="vip" className="mbu-slide" style={card(isTv ? 1250 : 940)}>
                    <h1 style={{ fontSize: isTv ? 46 : 30, fontWeight: 900, margin: "0 0 16px" }}>Want Your Bands Synced to the VIP Fireworks Shows?</h1>
          <p style={{ fontSize: S.body, lineHeight: 1.45, color: "#444", margin: "0 0 12px", textAlign: "center" }}>
            <b>Included with MagicBeacon:</b> Tink Alert, custom colors, alerts, timers &amp; more
          </p>
          <p style={{ fontSize: S.body, lineHeight: 1.45, color: "#444", margin: "0 0 18px", textAlign: "center" }}>
            <b>Add VIP Package for Live Show Sync:</b> your bands sync to LIVE fireworks shows in Magical MultiView and our Close-Up Fireworks Camera, both part of the VIP Package
          </p>
          <p style={{ fontSize: S.body, color: "#222", fontWeight: 700, margin: "0 0 26px" }}>
             VIP Package: {addonPrice}. Cancel anytime.
          </p>
          <div style={{ display: "flex", gap: isTv ? 24 : 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button style={btn("vipboth", "primary")} onClick={actions.vipboth}>
              ADD MAGICBEACON + VIP
            </button>
            <button style={btn("viponly")} onClick={actions.viponly}>
              JUST ADD MAGICBEACON
            </button>
          </div>
          <div style={{ marginTop: isTv ? 20 : 14 }}>
            <button style={btn("formback", "danger")} onClick={actions.formback}>
              GO BACK
            </button>
          </div>
        </div>
      )}
      {/* ===== Form step 1: agree ===== */}
      {screen === "form" && formStep === "agree" && (
        <div key="agree" className="mbu-slide" style={card(isTv ? 1100 : 760)}>
          <h1 style={{ fontSize: isTv ? 46 : 30, fontWeight: 900, margin: "0 0 8px" }}>MagicBeacon Package</h1>
          <div style={{ fontSize: isTv ? 28 : 19, color: "#2c3e50", marginBottom: isTv ? 26 : 18 }}>
            {addVip
              ? `Activate MagicBeacon + VIP on this device for ${addonEach}. Cancel anytime.`
              : `Activate MagicBeacon on this device for ${addonPrice}. Cancel anytime.`}
          </div>
          <button
            onClick={actions.agree}
            style={{
              display: "flex",
              alignItems: "center",
              gap: isTv ? 20 : 14,
              width: "100%",
              textAlign: "left",
              background: priceConfirmed ? "#EAF2FF" : "#F5F6FA",
              border: `${S.border}px solid ${isTv && focusId === "agree" ? LIME : agreementError ? "#e74c3c" : "#C9CEDB"}`,
              borderRadius: 14,
              padding: isTv ? "22px 24px" : "16px 18px",
              cursor: "pointer",
              outline: "none",
              fontFamily: "Lato, sans-serif",
            }}
          >
            <span
              style={{
                flex: "0 0 auto",
                width: isTv ? 48 : 34,
                height: isTv ? 48 : 34,
                borderRadius: 8,
                border: `${S.border}px solid ${BLUE}`,
                background: priceConfirmed ? BLUE : "#fff",
                color: "#fff",
                fontWeight: 900,
                fontSize: isTv ? 32 : 22,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {priceConfirmed ? "✓" : ""}
            </span>
            <span style={{ fontSize: isTv ? 28 : 19, color: "#333", lineHeight: 1.4 }}>
              {addVip
                ? `I understand the MagicBeacon package and the VIP Package will be added to this device's subscription for ${addonEach} (prorated today) and that I can cancel anytime.`
                : `I understand the MagicBeacon package will be added to this device's subscription for ${addonPrice} (prorated today) and that I can cancel anytime.`}
              <span style={{ display: "block", marginTop: 6, color: "#777", fontSize: isTv ? 22 : 15 }}>
                {isTv ? "Press OK to agree" : "Tap to agree"}
              </span>
            </span>
          </button>
          {agreementError && (
            <div style={{ color: "#e74c3c", fontSize: isTv ? 24 : 16, fontWeight: 700, marginTop: 10 }}>{agreementError}</div>
          )}
                    <div style={{ display: "flex", gap: isTv ? 24 : 14, justifyContent: "center", marginTop: isTv ? 28 : 20 }}>
            {!isTv && (
              <button style={btn("next", "primary", { opacity: priceConfirmed ? 1 : 0.6 })} onClick={goNext}>
                NEXT
              </button>
            )}
            <button style={btn("formback", "danger")} onClick={actions.formback}>
              GO BACK
            </button>
          </div>
        </div>
      )}

      {/* ===== Form step 2: last-4 digits ===== */}
      {screen === "form" && formStep === "digits" && (
        <div key="digits" className="mbu-slide" style={card(isTv ? 720 : 520)}>
          <h1 style={{ fontSize: isTv ? 40 : 26, fontWeight: 900, margin: "0 0 6px" }}>Confirm it's you</h1>
          <div style={{ fontSize: isTv ? 24 : 16, color: "#555", marginBottom: isTv ? 18 : 12 }}>
            Enter the last 4 digits of the phone number on your subscription.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: isTv ? 16 : 12 }}>
            {[0, 1, 2, 3].map((n) => (
              <div
                key={n}
                style={{
                  width: isTv ? 72 : 52,
                  height: isTv ? 82 : 58,
                  borderRadius: 12,
                  border: `2px solid ${phoneError ? "#e74c3c" : phoneLastDigits.length === n ? BLUE : "#ddd"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: isTv ? 40 : 28,
                  fontWeight: 900,
                }}
              >
                {phoneLastDigits[n] || ""}
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, width: isTv ? 480 : 340, margin: "0 auto" }}>
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "del"].map((k) => (
              <button
                key={k}
                onClick={actions[k]}
                style={{
                  height: isTv ? 62 : 52,
                  borderRadius: 12,
                  border: `${S.border}px solid ${isTv && focusId === k ? LIME : "transparent"}`,
                  background: "#EEF0F6",
                  fontFamily: "Lato, sans-serif",
                  fontSize: k === "clear" ? (isTv ? 22 : 16) : isTv ? 30 : 24,
                  fontWeight: 800,
                  color: "#222",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                {k === "del" ? "⌫" : k === "clear" ? "CLEAR" : k}
              </button>
            ))}
          </div>
          {phoneError && (
            <div style={{ color: "#e74c3c", fontSize: isTv ? 24 : 16, fontWeight: 700, marginTop: 10 }}>{phoneError}</div>
          )}
          <div style={{ display: "flex", gap: isTv ? 24 : 14, justifyContent: "center", marginTop: isTv ? 24 : 18 }}>
            <button
              style={btn("submit", "primary", { opacity: phoneLastDigits.length === 4 ? 1 : 0.6 })}
              onClick={actions.submit}
            >
              ACTIVATE NOW
            </button>
            <button style={btn("formback", "danger")} onClick={actions.formback}>
              GO BACK
            </button>
          </div>
        </div>
      )}

      {/* ===== Processing (right after submit) ===== */}
      {screen === "processing" && (
        <div style={card(isTv ? 900 : 640)}>
          <h1 style={{ fontSize: isTv ? 46 : 30, fontWeight: 900, margin: "0 0 14px" }}>Processing Your Request</h1>
          <div style={{ fontSize: S.body, color: "#666", marginBottom: 20 }}>
            Please wait while we activate MagicBeacon on this device...
          </div>
          <div
            style={{
              width: isTv ? 60 : 44,
              height: isTv ? 60 : 44,
              margin: "0 auto",
              border: "5px solid #f3f3f3",
              borderTop: "5px solid #3498db",
              borderRadius: "50%",
              animation: "mbuSpin 1.2s linear infinite",
            }}
          />
        </div>
      )}

      {/* ===== Status ===== */}
      {screen === "status" && (
        <div style={card(isTv ? 1200 : 900)}>
          <h1 style={{ fontSize: isTv ? 46 : 30, fontWeight: 900, margin: "0 0 14px" }}>MagicBeacon Activation</h1>
          <div style={{ fontSize: isTv ? 40 : 30, fontWeight: 900, color: statusColor, marginBottom: 12 }}>{statusLabel}</div>

          {status === "failed" ? (
            <div style={{ fontSize: S.body, color: "#e74c3c", marginBottom: 24, lineHeight: 1.45 }}>
              {upgradeRequest.error_message ? `Error: ${upgradeRequest.error_message}` : "Your activation couldn't be completed."}
            </div>
          ) : (
            <div style={{ fontSize: S.body, color: "#555", marginBottom: 24, lineHeight: 1.45 }}>
                            {statusBody}
            </div>
          )}
          {addVip && (
            <div style={{ fontSize: S.small, color: vipSent && vipSent !== "sent" ? "#e74c3c" : "#555", marginBottom: 20, lineHeight: 1.4 }}>
              {hasVipAddOn
                ? "VIP Package: added! Live Show Sync is unlocked."
                : vipSent === "sent"
                ? "VIP Package: being added to your subscription too."
                : vipSent
                ? `VIP Package couldn't be added (${vipSent}). You can add it anytime from the VIP button.`
                : "VIP Package: sending…"}
            </div>
          )}
          <div style={{ display: "flex", gap: isTv ? 28 : 16, justifyContent: "center" }}>
            {status === "failed" && (
              <button style={btn("retry", "primary")} onClick={actions.retry}>
                TRY AGAIN
              </button>
            )}
                        {isTv && (
              <button style={btn("close", status === "failed" ? "danger" : "secondary")} onClick={actions.close}>
                CLOSE
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BeaconUpgrade;
